import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api/client';
import { Customer, CustomerDetail } from '../../types';
import { DataTable } from '../../components/common/DataTable';
import { StatusBadge } from '../../components/common/StatusBadge';
import { Modal } from '../../components/common/Modal';
import { ColumnDef } from '@tanstack/react-table';
import {
  Ban,
  Pencil,
  Check,
  X,
  Phone,
  Receipt,
  FileText,
  Loader2,
  KeyRound,
  Trash2,
  History,
  MessageSquare,
  RotateCcw,
} from 'lucide-react';

export const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Inline edit (name + phone) — the fields support corrects after a call or from
  // an invoice. Email is the login identity and is not editable here.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', phoneNumber: '' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  // Message the customer directly — opens an agent support thread (their app +
  // a push), reusing the support system rather than a one-off notification.
  const [msg, setMsg] = useState({ subject: '', body: '' });
  const [msgSending, setMsgSending] = useState(false);

  // Deleted-accounts view: lists soft-deleted customers so a mistaken deletion
  // (a technician reaching for "sign out" hit "delete account") can be undone.
  // A deleted account can't be opened in detail (that query hides deleted rows),
  // so restore acts straight from the list row.
  const [deletedMode, setDeletedMode] = useState(false);
  const [pageMsg, setPageMsg] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);

  useEffect(() => {
    loadCustomers();
  }, [deletedMode]);

  const loadCustomers = async () => {
    try {
      setCustomers(deletedMode ? await api.getDeletedCustomers() : await api.getAllCustomers());
    } catch (err) {
      console.error('Failed to load customers', err);
    }
  };

  // Backend search: finds any customer by email/name/phone, not just the ones
  // already loaded. Empty term restores the full list. Honours the deleted view.
  const runSearch = async (term: string) => {
    try {
      if (deletedMode) {
        setCustomers(term.trim() ? await api.searchDeletedCustomers(term) : await api.getDeletedCustomers());
      } else {
        setCustomers(term.trim() ? await api.searchCustomers(term) : await api.getAllCustomers());
      }
    } catch (err) {
      console.error('Customer search failed', err);
    }
  };

  const handleRestore = async (cust: Customer) => {
    if (!window.confirm(
      `Restore ${cust.name || cust.email}? Their cameras, sharing and subscription are intact — ` +
      `they just need to sign in again (deletion signed them out everywhere).`,
    )) return;
    setRestoringId(cust.id);
    setPageMsg(null);
    try {
      await api.restoreCustomer(cust.id);
      setPageMsg(`Restored ${cust.email}. The account is live again.`);
      await loadCustomers(); // the restored account drops off the deleted list
    } catch (err: any) {
      setPageMsg(err?.message || 'Failed to restore the account.');
    } finally {
      setRestoringId(null);
    }
  };

  const sendMessage = async () => {
    if (!detail) return;
    const subject = msg.subject.trim();
    const body = msg.body.trim();
    if (!subject || !body) return;
    setMsgSending(true);
    setSaveError(null);
    try {
      await api.messageCustomer(detail.id, subject, body);
      setActionMsg('Message sent — it is in their app Support inbox and their phone was notified.');
      setMsg({ subject: '', body: '' });
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to send the message.');
    } finally {
      setMsgSending(false);
    }
  };

  const openDetail = async (cust: Customer) => {
    setSelectedCustomer(cust);
    setDetail(null);
    setEditing(false);
    setDetailError(null);
    setActionMsg(null);
    setMsg({ subject: '', body: '' });
    setDetailLoading(true);
    try {
      setDetail(await api.getCustomerDetail(cust.id));
    } catch (err: any) {
      setDetailError(err?.message || 'Failed to load customer');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedCustomer(null);
    setDetail(null);
    setEditing(false);
    setSaveError(null);
  };

  const startEdit = () => {
    if (!detail) return;
    setForm({
      firstName: detail.firstName ?? '',
      lastName: detail.lastName ?? '',
      phoneNumber: detail.phone ?? '',
    });
    setSaveError(null);
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!detail) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.updateCustomer(detail.id, {
        firstName: form.firstName,
        lastName: form.lastName,
        phoneNumber: form.phoneNumber,
      });
      setDetail(updated);
      setEditing(false);
      loadCustomers(); // reflect the new name/phone in the list
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!detail) return;
    const next = !detail.isBlocked;
    const reason = next
      ? window.prompt('Reason for blocking this account?') || ''
      : '';
    if (next && !reason.trim()) return; // block needs a reason
    try {
      await api.toggleCustomerBlock(detail.id, next, reason);
      setDetail(await api.getCustomerDetail(detail.id));
      loadCustomers();
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to change block state');
    }
  };

  const handleResetPassword = async () => {
    if (!detail) return;
    const pw = window.prompt(`Set a new password for ${detail.email} (min 8 chars). Share it with the customer; they can change it after login.`);
    if (pw == null) return;
    if (pw.length < 8) { setSaveError('Password must be at least 8 characters.'); return; }
    setSaveError(null);
    try {
      await api.resetCustomerPassword(detail.id, pw);
      setActionMsg('Password reset. Give the new password to the customer.');
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to reset password');
    }
  };

  const handleDeleteCamera = async (camId: string, camName: string) => {
    if (!detail) return;
    if (!window.confirm(`Remove "${camName || camId}" from the platform? It can be re-added afterwards.`)) return;
    try {
      await api.adminDeleteDevice(camId);
      setActionMsg(`Removed ${camName || camId}.`);
      setDetail(await api.getCustomerDetail(detail.id));
      loadCustomers();
    } catch (err: any) {
      setSaveError(err?.message || 'Failed to remove camera');
    }
  };

  const money = (n: number, ccy: string) =>
    `${ccy === 'USD' ? '$' : ccy + ' '}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const columns = useMemo<ColumnDef<Customer>[]>(
    () => [
      {
        id: 'customer',
        // Searchable value includes email + phone (not just the display name) so
        // a search by email address matches.
        accessorFn: (c: Customer) => `${c.name} ${c.email} ${c.phone ?? ''}`,
        header: 'Customer Account',
        cell: (info) => {
          const c = info.row.original;
          return (
            <div>
              <div style={{ fontWeight: 600, color: 'var(--text-main)' }}>{c.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                {c.email}{c.phone ? ` • ${c.phone}` : ''}
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'cameraCount',
        header: 'Cameras',
        cell: (info) => (
          <span style={{ fontWeight: 600, color: 'var(--primary-light)' }}>{info.getValue() as number}</span>
        ),
      },
      {
        accessorKey: 'activePlan',
        header: 'Active Subscription',
        cell: (info) => <span style={{ fontWeight: 500 }}>{info.getValue() as string}</span>,
      },
      {
        accessorKey: 'joinedAt',
        header: 'Registered',
        cell: (info) => {
          const v = info.getValue() as string;
          return <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(v)}</span>;
        },
      },
      {
        accessorKey: 'billingStatus',
        header: 'Billing Status',
        cell: (info) => <StatusBadge status={info.getValue() as string} />,
      },
      {
        accessorKey: 'isBlocked',
        header: 'Access',
        cell: (info) => <StatusBadge status={(info.getValue() as boolean) ? 'blocked' : 'active'} />,
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const c = info.row.original;
          if (deletedMode) {
            return (
              <button
                className="btn btn-primary btn-sm"
                disabled={restoringId === c.id}
                onClick={() => handleRestore(c)}
                title="Restore this deleted account"
              >
                {restoringId === c.id ? <Loader2 size={13} className="spin" /> : <RotateCcw size={13} />}
                <span>Restore</span>
              </button>
            );
          }
          return (
            <button className="btn btn-secondary btn-sm" onClick={() => openDetail(c)}>
              Open
            </button>
          );
        },
      },
    ],
    [deletedMode, restoringId],
  );

  const label = (t: string) => (
    <div style={{ fontSize: 11, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 }}>{t}</div>
  );

  return (
    <div>
      {pageMsg && (
        <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success-border)', color: 'var(--success-text)', padding: '9px 14px', borderRadius: 'var(--radius-md)', marginBottom: 12, fontSize: 12.5 }}>
          {pageMsg}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {deletedMode
            ? 'Showing deleted accounts — restore one to bring it back (its cameras & sharing are intact).'
            : ''}
        </div>
        <button
          className={`btn btn-sm ${deletedMode ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => { setPageMsg(null); setDeletedMode((v) => !v); }}
          title="Show accounts that were deleted, so a mistaken deletion can be restored"
        >
          {deletedMode ? <><X size={14} /> <span>Exit deleted view</span></> : <><RotateCcw size={14} /> <span>Deleted accounts</span></>}
        </button>
      </div>
      <DataTable
        storageKey="customers"
        data={customers}
        columns={columns}
        searchPlaceholder={deletedMode ? 'Search deleted accounts by name, email, phone...' : 'Search customer name, email, phone...'}
        exportFilename="mwm_customers_list.csv"
        onSearch={runSearch}
      />

      <Modal
        isOpen={!!selectedCustomer}
        onClose={closeDetail}
        title={detail ? `${detail.firstName ?? ''} ${detail.lastName ?? ''}`.trim() || detail.email : selectedCustomer?.name || 'Customer'}
        subtitle={selectedCustomer ? `Account ID: ${selectedCustomer.id}` : ''}
        size="large"
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: '10px' }}>
              {!editing && detail && (
                <button className="btn btn-secondary btn-sm" onClick={startEdit}>
                  <Pencil size={14} /> <span>Edit info</span>
                </button>
              )}
              {detail && (
                <button
                  className={`btn btn-sm ${detail.isBlocked ? 'btn-secondary' : 'btn-danger'}`}
                  onClick={handleToggleBlock}
                >
                  <Ban size={14} /> <span>{detail.isBlocked ? 'Unblock' : 'Block'}</span>
                </button>
              )}
              {detail && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleResetPassword}
                  title={detail.hasPassword ? 'Set a new password for this email account' : 'This is a social login — setting a password adds email/password login too'}
                >
                  <KeyRound size={14} /> <span>Reset password</span>
                </button>
              )}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={closeDetail}>Close</button>
          </div>
        }
      >
        {detailLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            <Loader2 size={22} className="spin" />
          </div>
        ) : detailError ? (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)', padding: '14px 18px', borderRadius: 'var(--radius-md)', fontSize: 13 }}>
            {detailError}
          </div>
        ) : detail ? (
          <div>
            {actionMsg && (
              <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success-border)', color: 'var(--success-text)', padding: '9px 14px', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12.5 }}>
                {actionMsg}
              </div>
            )}
            {/* Overview grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              {/* Contact — editable */}
              <div className="card" style={{ padding: '14px' }}>
                {label('Contact')}
                {editing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input className="input" placeholder="First name" value={form.firstName}
                        onChange={(e) => setForm({ ...form, firstName: e.target.value })} style={inp} />
                      <input className="input" placeholder="Last name" value={form.lastName}
                        onChange={(e) => setForm({ ...form, lastName: e.target.value })} style={inp} />
                    </div>
                    <input className="input" placeholder="Phone number" value={form.phoneNumber}
                      onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} style={inp} />
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{detail.email} (login — not editable)</div>
                    {saveError && <div style={{ color: 'var(--danger-text)', fontSize: 12 }}>{saveError}</div>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-primary btn-sm" onClick={saveEdit} disabled={saving}>
                        {saving ? <Loader2 size={13} className="spin" /> : <Check size={13} />} <span>Save</span>
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>
                        <X size={13} /> <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontWeight: 600, fontSize: 13, marginTop: 2 }}>{detail.email}</div>
                    <div style={{ fontSize: 12.5, color: detail.phone ? 'var(--text-main)' : 'var(--text-dim)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Phone size={12} />
                      {detail.phone
                        ? <a href={`tel:${detail.phone}`} style={{ color: 'var(--primary-light)', textDecoration: 'none' }}>{detail.phone}</a>
                        : 'No phone on file'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 6 }}>
                      Login: {[
                        detail.hasPassword ? 'Email/password' : null,
                        detail.loginGoogle ? 'Google' : null,
                        detail.loginApple ? 'Apple' : null,
                      ].filter(Boolean).join(' · ') || 'unknown'}
                    </div>
                  </>
                )}
              </div>

              {/* Plan */}
              <div className="card" style={{ padding: '14px' }}>
                {label('Plan')}
                <div style={{ fontWeight: 600, fontSize: 13, marginTop: 2 }}>
                  {detail.subscription?.planName || 'No subscription'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  {detail.subscription
                    ? <>Status: {detail.subscription.status}{detail.subscription.planPrice != null ? ` · ${money(detail.subscription.planPrice, detail.subscription.currency || 'USD')}${detail.subscription.billingCycle ? '/' + detail.subscription.billingCycle : ''}` : ''}</>
                    : 'Never subscribed'}
                </div>
                {detail.subscription && (detail.subscription.currentPeriodStart || detail.subscription.currentPeriodEnd) && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 4 }}>
                    {detail.subscription.currentPeriodStart ? `Since ${fmtDate(detail.subscription.currentPeriodStart)}` : ''}
                    {detail.subscription.currentPeriodEnd ? `${detail.subscription.currentPeriodStart ? ' · ' : ''}${detail.subscription.cancelAtPeriodEnd ? 'ends' : 'renews'} ${fmtDate(detail.subscription.currentPeriodEnd)}` : ''}
                  </div>
                )}
              </div>

              {/* Totals */}
              <div className="card" style={{ padding: '14px' }}>
                {label('Account')}
                <div style={{ fontSize: 12.5, marginTop: 2 }}>
                  {detail.totals.cameras} cameras · {detail.totals.camerasOnline} online
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                  Paid to date: <strong>{money(detail.totals.paidToDate, detail.totals.currency)}</strong>
                  {detail.totals.pendingAmount > 0 && (
                    <span style={{ color: 'var(--text-dim)' }} title="Online checkouts the customer started but never completed. Not money owed — record their invoice payment instead.">
                      {' · '}{money(detail.totals.pendingAmount, detail.totals.currency)} in unfinished checkouts
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-dim)', marginTop: 6 }}>
                  Registered {fmtDate(detail.createdAt)}
                  {detail.lastLoginAt ? ` · last login ${fmtDate(detail.lastLoginAt)}` : ''}
                </div>
              </div>
            </div>

            {detail.isBlocked && detail.blocked && (
              <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius-md)', fontSize: 12.5, marginBottom: 20 }}>
                <Ban size={13} style={{ verticalAlign: -2, marginRight: 6 }} />
                Blocked{detail.blocked.reason ? `: ${detail.blocked.reason}` : ''}{detail.blocked.at ? ` (${new Date(detail.blocked.at).toLocaleDateString()})` : ''}
              </div>
            )}

            {/* Cameras */}
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
                Cameras ({detail.cameras.length})
              </h4>
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '4px 14px' }}>
                {detail.cameras.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '10px 0' }}>No cameras.</div>
                ) : detail.cameras.map((cam) => (
                  <div key={cam.id} style={rowStyle}>
                    <div><strong style={{ fontSize: 12.5 }}>{cam.name || '(unnamed)'}</strong> <code style={{ fontSize: 11, color: 'var(--primary-light)' }}>{cam.serialNumber}</code></div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <StatusBadge status={cam.isOnline ? 'online' : 'offline'} />
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => handleDeleteCamera(cam.id, cam.name)}
                        title="Remove this camera from the platform (can be re-added)"
                        style={{ color: 'var(--danger-text)', padding: 4 }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payments — includes manually-recorded invoice payments */}
            <div>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Receipt size={14} /> Payments ({detail.payments.length})
              </h4>
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '4px 14px' }}>
                {detail.payments.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--text-dim)', padding: '10px 0' }}>No payments recorded.</div>
                ) : detail.payments.map((p) => (
                  <div key={p.id} style={rowStyle}>
                    <div style={{ minWidth: 0 }}>
                      <strong style={{ fontSize: 12.5 }}>{money(p.amount, p.currency)}</strong>
                      {p.provider && (
                        <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 999, background: p.provider === 'manual' ? 'var(--warning-bg)' : 'var(--info-bg)', color: p.provider === 'manual' ? 'var(--warning-text)' : 'var(--info-text)', border: `1px solid ${p.provider === 'manual' ? 'var(--warning-border)' : 'var(--info-border)'}` }}>
                          {p.provider === 'manual' ? 'invoice / manual' : p.provider}
                        </span>
                      )}
                      {p.notes && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}> — {p.notes}</span>}
                      {p.invoiceUrl && (
                        <a href={p.invoiceUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 8, fontSize: 11, color: 'var(--primary-light)', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                          <FileText size={11} /> invoice
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : ''}</span>
                      <StatusBadge status={p.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Plan history — subscription lifecycle (v2-era). Pre-v2 plan
                changes lived in the old system and aren't shown here. */}
            {detail.planHistory.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <History size={14} /> Plan history ({detail.planHistory.length})
                </h4>
                <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '4px 14px' }}>
                  {detail.planHistory.map((h, i) => (
                    <div key={i} style={rowStyle}>
                      <div style={{ minWidth: 0 }}>
                        <strong style={{ fontSize: 12.5 }}>{planActionLabel(h.action)}</strong>
                        {(h.fromPlan || h.toPlan) && (
                          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>
                            {h.fromPlan ? `${h.fromPlan} → ` : ''}{h.toPlan || ''}
                          </span>
                        )}
                      </div>
                      <span style={{ fontSize: 11, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{fmtDate(h.createdAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Message the customer directly — opens a support thread (in their
                app's Support inbox) and pushes their phone. Reuses support. */}
            <div style={{ marginTop: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <MessageSquare size={14} /> Message customer
              </h4>
              <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input
                  className="input"
                  style={{ ...inp, flex: 'unset' }}
                  placeholder="Subject"
                  maxLength={200}
                  value={msg.subject}
                  onChange={(e) => setMsg({ ...msg, subject: e.target.value })}
                />
                <textarea
                  className="input"
                  style={{ ...inp, flex: 'unset', minHeight: 72, resize: 'vertical', fontFamily: 'inherit' }}
                  placeholder="Write a message… it opens a Support thread in their app and notifies their phone. They can reply."
                  value={msg.body}
                  onChange={(e) => setMsg({ ...msg, body: e.target.value })}
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={sendMessage}
                    disabled={msgSending || !msg.subject.trim() || !msg.body.trim()}
                  >
                    {msgSending ? <Loader2 size={13} className="spin" /> : <MessageSquare size={13} />}
                    <span>{msgSending ? 'Sending…' : 'Send message'}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

// Friendly labels for subscription_audit_log action codes. Unknown codes fall
// back to a de-underscored title-case so a new action still reads sensibly.
const PLAN_ACTION_LABELS: Record<string, string> = {
  subscribed: 'Subscribed',
  subscribed_pending: 'Subscription started (pending)',
  mandate_setup: 'Payment mandate set up',
  plan_change_staged: 'Plan change staged',
  store_order_created: 'Store order created',
  'soxara_payment.failed': 'Payment failed',
  created: 'Created',
  upgraded: 'Upgraded',
  downgraded: 'Downgraded',
  canceled: 'Canceled',
  renewed: 'Renewed',
  manual_activated: 'Manually activated',
};
const planActionLabel = (a: string): string =>
  PLAN_ACTION_LABELS[a] ?? a.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Short absolute date (e.g. "5 Aug 2026"). '—' when missing/unparseable, so an
// operator never sees "Invalid Date".
const fmtDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const inp: React.CSSProperties = {
  flex: 1, minWidth: 0, width: '100%', boxSizing: 'border-box',
  background: 'var(--bg-surface)', border: '1px solid var(--border-dim)',
  borderRadius: 'var(--radius-sm)', padding: '7px 10px', color: 'var(--text-main)', fontSize: 13,
};
const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 12.5, gap: 10,
};
